import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const NOTICE_TABLE_COLUMNS = [
    { width: 70 },
    { width: 260 },
    { width: 360 },
    { width: 110 },
    { width: 180 },
    { width: 180 },
    { width: 120 },
]

const NOTICE_TABLE_MIN_WIDTH = NOTICE_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const NOTICE_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const NOTICE_STATUS_CELL_SX = {
    whiteSpace: 'nowrap',
}

const NOTICE_DATE_CELL_SX = {
    whiteSpace: 'nowrap',
}

export default function Notices() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const deleteInFlightRef = useRef(new Set())
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(null)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/notice/getNoticeList', { page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect })

            if (data.code === 1) {
                setTotalCount(data.data.count || 0)
                setData(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '공지사항 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '공지사항 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const isBusy = Boolean(actionLoading)

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || String(searchSelect.searchStatus) !== String(DEFAULT_SEARCH_SELECT.searchStatus)

    // 조회 실패와 필터 결과 없음을 같은 테이블 영역에서 구분한다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="공지사항 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading || isBusy}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 공지사항이 없습니다."
                    description="제목/내용 또는 노출상태 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 공지사항이 없습니다."
                description="공지사항을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    const updateSearchSelect = (e) => {
        if (isBusy) {
            return
        }

        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
        setCurPage(1)
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (isBusy) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current.has(id)) {
            return
        }

        try {
            deleteInFlightRef.current.add(id)
            setActionLoading({ type: 'delete', id })

            const { data: response } = await API.post('/api/v1/admin/notice/delete', { noticeId: id })

            if (response.code === 1) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('공지사항을 삭제했습니다.')

                if (curPage > 1 && data.length === 1) {
                    setCurPage((prev) => prev - 1)
                } else {
                    await getData(false)
                }
            } else if (response.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(response.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('공지사항을 삭제하지 못했습니다.')
        } finally {
            deleteInFlightRef.current.delete(id)
            setActionLoading(null)
        }
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>공지사항 리스트</Title>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="제목/내용"
                                type="search"
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={isBusy}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <TextField
                                label="노출상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                                disabled={isBusy}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value='1'>노출</MenuItem>
                                <MenuItem value='0'>미노출</MenuItem>
                            </TextField>
                        </ListFilterForm>

                    </ListFilterBar>

                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={isBusy}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({ pathname: '/new-notice', search: location.search, curPage: curPage, searchText: searchQuery, searchSelect: searchSelect }))}
                            >
                                등록
                            </Button>
                        )}
                    />

                    <ListTableContainer>
                        <Table size="small" sx={{ minWidth: NOTICE_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                            <colgroup>
                                {NOTICE_TABLE_COLUMNS.map((column, index) => (
                                    <col key={`notice-column-${index}`} style={{ width: column.width }} />
                                ))}
                            </colgroup>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>제목</TableCell>
                                    <TableCell>내용</TableCell>
                                    <TableCell>노출여부</TableCell>
                                    <TableCell>등록일</TableCell>
                                    <TableCell>수정일</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.length > 0 ? (
                                    data.map((row) => (
                                        <TableRow key={row.noticeId}>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell sx={NOTICE_TEXT_CELL_SX}>{row.title || '-'}</TableCell>
                                            <TableCell className="listTableLimitText" sx={NOTICE_TEXT_CELL_SX}>{row.content || '-'}</TableCell>
                                            <TableCell sx={NOTICE_STATUS_CELL_SX}>{row.status === 1 ? <VisibilityIcon/> : <VisibilityOffIcon/>}</TableCell>
                                            <TableCell sx={NOTICE_DATE_CELL_SX}>{renderDateTime(row.regTime)}</TableCell>
                                            <TableCell sx={NOTICE_DATE_CELL_SX}>{renderDateTime(row.updTime)}</TableCell>
                                            <TableCell align="right">
                                                <ListActionGroup disabled={isBusy}>
                                                    <EditIcon path={'notice'} id={row.noticeId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect}/>
                                                    <DeleteAlertDialog handleDelete={handleDelete} id={row.noticeId}/>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
